import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isMobileContainer } from '../../utils/device';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { formatDate } from '../../utils/appDateTime';

function StatPill({ label, value }) {
  return (
    <div className="rounded-md border border-border px-3 py-2 text-center">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}

function PeriodAxisTick({ x, y, payload, rows }) {
  const row = rows.find((r) => r.month_label === payload?.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize={9}>
        {payload?.value}
      </text>
      {row?.release_date && (
        <text x={0} y={0} dy={24} textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize={8}>
          {formatDate(row.release_date)}
        </text>
      )}
    </g>
  );
}

function AttendanceTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2 text-xs shadow-md">
      <p className="font-medium text-foreground">{row.month_label}</p>
      <p className="text-muted-foreground mt-1">Attendance: {row.attendance_percentage}%</p>
      {row.release_date && (
        <p className="text-muted-foreground">Salary date: {formatDate(row.release_date)}</p>
      )}
    </div>
  );
}

export default function CrmEmployeeAttendanceChart({ employeeId }) {
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [periods, setPeriods] = useState([]);
  const [summary, setSummary] = useState({});
  const [trendRows, setTrendRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const chartHeight = isMobileContainer() ? 'h-56' : 'h-64';

  const loadData = useCallback(async (start, end) => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const params = start && end ? { period_start: start, period_end: end } : { view: 'period' };
      const [historyRes, analyticsRes, periodsRes] = await Promise.all([
        api.get(`/crm/employees/${employeeId}/attendance`, { params }),
        api.get(`/crm/employees/${employeeId}/attendance/analytics`),
        api.get(`/crm/employees/${employeeId}/attendance/periods`),
      ]);
      const data = historyRes.data?.data || {};
      const periodList = periodsRes.data?.data?.periods || [];
      setSummary(data.summary || {});
      setPeriodStart(data.period_start || '');
      setPeriodEnd(data.period_end || '');
      setTrendRows(analyticsRes.data?.data?.monthly_rows || []);
      setPeriods(periodList);

      const matchedPeriod = periodList.find(
        (p) => p.period_start === data.period_start && p.period_end === data.period_end,
      );
      setReleaseDate(data.release_date || matchedPeriod?.release_date || '');
    } catch {
      setSummary({});
      setTrendRows([]);
      setReleaseDate('');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const currentIndex = periods.findIndex((p) => p.period_start === periodStart && p.period_end === periodEnd);
  const navigatePeriod = (delta) => {
    if (currentIndex < 0 || periods.length === 0) return;
    const next = periods[currentIndex + delta];
    if (!next) return;
    loadData(next.period_start, next.period_end);
  };

  const attendancePct = summary.attendance_percentage ?? summary.average_percent ?? 0;

  const axisTick = useMemo(
    () => (props) => <PeriodAxisTick {...props} rows={trendRows} />,
    [trendRows],
  );

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-base">Attendance</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={loading || currentIndex <= 0}
              onClick={() => navigatePeriod(-1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-sm">
              <p className="font-medium text-foreground">Salary cycle</p>
              <p className="text-muted-foreground">{formatDate(periodStart)} – {formatDate(periodEnd)}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={loading || currentIndex < 0 || currentIndex >= periods.length - 1}
              onClick={() => navigatePeriod(1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="text-sm sm:text-right">
            <p className="font-medium text-foreground">Salary date</p>
            <p className="text-muted-foreground">{formatDate(releaseDate)}</p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading attendance…</p>
        ) : (
          <>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Attendance percentage</span>
                <span className="text-foreground font-semibold">{attendancePct}%</span>
              </div>
              <Progress value={attendancePct} className="h-2" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <StatPill label="Working days" value={summary.working_days ?? 0} />
              <StatPill label="Present days" value={summary.present_days ?? summary.present ?? 0} />
              <StatPill label="Absent" value={summary.absent_days ?? summary.absent ?? 0} />
              <StatPill label="Approved leave" value={summary.approved_leave ?? summary.leave ?? 0} />
              <StatPill label="LWP" value={summary.lwp_days ?? summary.lwp ?? 0} />
              <StatPill label="Holidays" value={summary.holidays ?? 0} />
              <StatPill label="Week offs" value={summary.week_offs ?? 0} />
              <StatPill label="Payable days" value={summary.payable_days ?? 0} />
              <StatPill label="Late" value={summary.late ?? 0} />
            </div>

            <div>
              <p className="text-sm font-medium mb-3">Recent salary periods</p>
              {trendRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No trend data yet.</p>
              ) : (
                <div className={chartHeight}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendRows} margin={{ bottom: 28 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month_label" tick={axisTick} height={48} interval={0} />
                      <YAxis allowDecimals={false} domain={[0, 100]} unit="%" />
                      <Tooltip content={<AttendanceTooltip />} />
                      <Bar dataKey="attendance_percentage" fill="#10b981" name="Attendance %" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
